// src/pages/Dashboard.js
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOAuth2Monitor } from '../hooks/useOAuth2Monitor';
import OAuth2StatusPanel from '../components/monitoring/OAuth2StatusPanel';
import { useStoreStats, useTopProducts, useDailySales } from '../hooks/useApiWithStore';
import { useStoreId } from '../contexts/StoreContext';
import { 
  Alert,
  Container, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import { Assessment } from '@mui/icons-material';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

// Componentes internos
import MetricCardMUI from '../components/ui/MetricCardMUI';
import RangeSelector from '../components/ui/RangeSelector';
import useDefaultRange from '../hooks/useDefaultRange';
import SalesChartCard from '../components/Dashboard/cards/SalesChartCard';

import TopProductsChartCard from '../components/Dashboard/cards/TopProductsChartCard';
import TopProductsTableCard from '../components/Dashboard/cards/TopProductsTableCard';
import AbandonedCartsCard from '../components/Dashboard/cards/AbandonedCartsCard';
import RecommendationsCard from '../components/Dashboard/cards/RecommendationsCard';
import QuickActionsCard from '../components/Dashboard/cards/QuickActionsCard';
import AlertsCard from '../components/Dashboard/cards/AlertsCard';
import DailySalesCard from '../components/Dashboard/cards/DailySalesCard';
import ProfitabilityAnalysisCard from '../components/Dashboard/cards/ProfitabilityAnalysisCard';
import CustomerSegmentationCard from '../components/Dashboard/cards/CustomerSegmentationCard';
import PredictiveInsightsCard from '../components/Dashboard/cards/PredictiveInsightsCard';
import CompetitiveAnalysisCard from '../components/Dashboard/cards/CompetitiveAnalysisCard';
import AdvancedAnalyticsCard from '../components/Dashboard/cards/AdvancedAnalyticsCard';
import CustomerInsightsCard from '../components/Dashboard/cards/CustomerInsightsCard';
import InventoryInsightsCard from '../components/Dashboard/cards/InventoryInsightsCard';
import PerformanceInsightsCard from '../components/Dashboard/cards/PerformanceInsightsCard';
import RevenueTrendsCard from '../components/Dashboard/cards/RevenueTrendsCard';
import ReportsAccessCard from '../components/Dashboard/cards/ReportsAccessCard';
import PageHeader from '../components/ui/PageHeader';
import { DESIGN_SYSTEM } from '../theme/designSystem';
import LoadingScreen from '../components/LoadingScreen';
import DashboardSkeleton from '../components/Dashboard/DashboardSkeleton';

import './Dashboard.css';

// Datos de ejemplo para gráficos
const COLORS = ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b'];


const SimpleBarChart = ({ data, xKey, yKey, color = '#4e73df' }) => (
  <div style={{ height: '300px', width: '100%' }}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis 
          dataKey={xKey} 
          tick={{ fill: '#6c757d' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis 
          tick={{ fill: '#6c757d' }}
          axisLine={false}
          tickLine={false}
          width={80}
          tickFormatter={(value) => 
            new Intl.NumberFormat('es-AR', { 
              style: 'currency', 
              currency: 'ARS',
              maximumFractionDigits: 0
            }).format(value)
          }
        />
        <Tooltip 
          formatter={(value) => [
            new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value),
            'Ventas'
          ]}
        />
        <Bar dataKey={yKey} fill={color} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const SimplePieChart = ({ data, dataKey, nameKey }) => {
  return (
    <div style={{ height: '250px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={90}
            paddingAngle={5}
            dataKey={dataKey}
            nameKey={nameKey}
            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip 
            formatter={(value, name) => [
              new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value),
              name
            ]}
          />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

function Dashboard() {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const { rangeKey, startDate, endDate, setRange } = useDefaultRange();
  const { storeId, isLoading: storeIdLoading, error: storeIdError } = useStoreId();

  // Monitoreo OAuth2 para dashboard principal
  const { 
    metrics: oauth2Metrics, 
    isCritical, 
    hasWarnings, 
    getRecentErrors 
  } = useOAuth2Monitor();
  
  // Logging optimizado de errores OAuth2 para debugging
  useEffect(() => {
    // Solo loggear cambios de estado críticos para evitar spam
    if (isCritical() && oauth2Metrics.consecutiveErrors > 0) {
      console.error('[Dashboard] OAuth2 Critical state:', {
        systemHealth: oauth2Metrics.systemHealth,
        consecutiveErrors: oauth2Metrics.consecutiveErrors,
        errorRate: oauth2Metrics.errorRate?.toFixed(2) + '%'
      });
    }
  }, [isCritical, oauth2Metrics.systemHealth, oauth2Metrics.consecutiveErrors]);

  // Usar hooks centralizados con storeId automático
  const {
    data: statsData,
    isLoading: statsLoading,
    error: statsError,
  } = useStoreStats(
    { startDate, endDate },
    {
      retry: 2,
      refetchOnWindowFocus: false
    }
  );

  const {
    data: topProductsData,
    isLoading: productsLoading,
    error: productsError,
  } = useTopProducts(
    { startDate, endDate, limit: 5 },
    {
      retry: 2,
      refetchOnWindowFocus: false
    }
  );
  
  // Extraer productos del formato de respuesta
  const topProducts = topProductsData?.items || [];

  // Queries antiguas eliminadas - ahora usamos hooks centralizados

  // Manejo de errores del storeId
  if (storeIdLoading) {
    return <DashboardSkeleton />;
  }
  
  if (storeIdError || !storeId) {
    return (
      <Container className="mt-4">
        <Alert severity="error">
          <strong>Error de configuración:</strong> {storeIdError || 'No se pudo obtener el ID de la tienda'}
          <br />
          <small>StoreId actual: {storeId || 'null'}</small>
        </Alert>
      </Container>
    );
  }

  const loading = statsLoading || productsLoading;
  const error = statsError || productsError;
  
  // Validación y normalización de datos
  const stats = statsData || {};
  const validatedTopProducts = Array.isArray(topProducts) ? topProducts : [];

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (error) {
    console.error('[Dashboard] Error loading data:', error);
    return (
      <div className="alert alert-danger" role="alert">
        <i className="bi bi-exclamation-triangle-fill me-2"></i>
        Error al cargar los datos del dashboard. Por favor, intenta nuevamente.
      </div>
    );
  }

  // Datos para gráficos - simplificado por ahora
  
  // DATOS DE VENTAS DIARIAS - SOLO DATOS REALES DEL BACKEND
  const salesData = [];
  // TODO: Implementar endpoint real /stats/daily cuando esté listo
  // Por ahora no mostrar datos falsos

  // Debug: Logging de datos para diagnóstico
  console.log('[Dashboard] Datos recibidos:', {
    productos: {
      count: validatedTopProducts.length,
      hasRealData: validatedTopProducts.some(p => p.totalRevenue > 0 || p.revenuePaid > 0)
    },
    ventasDiarias: {
      count: salesData.length,
      hasData: salesData.length > 0,
      sample: salesData.slice(0, 3)
    },
    statsData: statsData
  });

  const productData = validatedTopProducts
    .filter(product => product && (product.name || product.productTitle))
    .slice(0, 5) // Limitar a máximo 5 productos
    .map(product => {
      const productName = product.name || product.productTitle || 'Sin nombre';
      return {
        name: typeof productName === 'string' && productName.length > 15 
          ? `${productName.substring(0, 15)}...` 
          : productName,
        value: Number(product.revenuePaid || product.totalRevenue) || 0
      };
    });

  const theme = useTheme();

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Alertas OAuth2 */}
      {isCritical() && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Sistema de autenticación crítico:</strong> Se detectaron múltiples errores de OAuth2. 
          Revisa la conectividad con Tiendanube.
        </Alert>
      )}
      
      {hasWarnings() && !isCritical() && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Advertencia de autenticación:</strong> Se detectaron algunos errores de OAuth2. 
          El sistema está funcionando pero requiere atención.
        </Alert>
      )}
      
      {/* Header Unificado */}
      <PageHeader 
        title={DESIGN_SYSTEM.pageHeaders.dashboard.title}
        subtitle={DESIGN_SYSTEM.pageHeaders.dashboard.subtitle}
        icon={<Assessment />}
        actions={
          <Box display="flex" alignItems="center" gap={2}>
            <Chip 
              label={userData?.plan || 'Free'} 
              color={userData?.plan === 'Free' ? 'default' : 'primary'}
              size="small"
            />
            <Typography variant="caption" color="text.secondary">
              {new Date(startDate).toLocaleDateString('es-AR')} - {new Date(endDate).toLocaleDateString('es-AR')}
            </Typography>
          </Box>
        }
      />
      {/* Selector de rango */}
      <RangeSelector value={rangeKey} onChange={({ key, startDate: s, endDate: e }) => setRange({ rangeKey: key, startDate: s, endDate: e })} />

      {/* Panel de diagnóstico del StoreId (solo en desarrollo) */}
      {process.env.NODE_ENV === 'development' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Diagnóstico StoreId:</strong> {storeId}
          <br />
          <small>Stats: {statsData ? 'OK' : 'Sin datos'} | Productos: {topProducts.length} items</small>
        </Alert>
      )}

      {/* Métricas Principales */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCardMUI 
            title="Ventas Totales" 
            value={stats?.totalSales || 0}
            change={stats?.salesChange !== null ? stats?.salesChange : null}
            icon="currency-dollar"
            color={theme.palette.primary.main}
            prefix="$"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCardMUI 
            title="Órdenes" 
            value={stats?.totalOrders || 0}
            change={stats?.ordersChange !== null ? stats?.ordersChange : null}
            icon="cart"
            color={theme.palette.success.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCardMUI 
            title="Ticket Promedio" 
            value={stats?.averageOrderValue || 0}
            change={stats?.aovChange !== null ? stats?.aovChange : null}
            icon="receipt"
            color={theme.palette.info.main}
            prefix="$"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCardMUI 
            title="Tasa de Conversión" 
            value={(stats?.conversionRate || 0).toFixed(2)}
            change={stats?.conversionRateChange !== null ? stats?.conversionRateChange : null}
            icon="percent"
            color={theme.palette.warning.main}
            suffix="%"
          />
        </Grid>
      </Grid>

      {/* Métricas Secundarias */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCardMUI 
            title="Unidades" 
            value={stats?.totalUnits ?? 0}
            change={stats?.unitsChange ?? 0}
            icon="boxes"
            color={theme.palette.success.light}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCardMUI 
            title="Carritos Creados" 
            value={stats?.totalCarts ?? 0}
            change={stats?.cartsChange ?? 0}
            icon="cart-plus"
            color={theme.palette.info.light}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCardMUI 
            title="Nuevos Clientes" 
            value={stats?.newCustomers ?? 0}
            change={stats?.newCustomersChange ?? 0}
            icon="person-plus"
            color={theme.palette.secondary.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCardMUI 
            title="% Recurrentes" 
            value={(stats?.repeatCustomerRate ?? 0).toFixed?.(1) ?? 0}
            change={stats?.repeatCustomerRateChange ?? 0}
            icon="repeat"
            color={theme.palette.warning.light}
            suffix="%"
          />
        </Grid>
      </Grid>

      {/* Sección Principal: Ventas y Productos */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={8}>
          <Card sx={{ 
            height: 400, 
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': { transform: 'translateY(-2px)' }
          }} onClick={() => navigate('/stats')}>
            <CardContent sx={{ p: 3, height: '100%' }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Assessment sx={{ color: theme.palette.primary.main }} />
                <Typography variant="h6" fontWeight={600}>
                  Ventas Diarias
                </Typography>
              </Box>
              
              {/* Usar datos reales de stats en lugar de dailySales */}
              {statsData && statsData.dailySales && statsData.dailySales.length > 0 ? (
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={statsData.dailySales.slice(-7)}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis 
                        dataKey="fecha" 
                        tick={{ fill: '#6c757d', fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(value) => new Date(value).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })}
                      />
                      <YAxis 
                        tick={{ fill: '#6c757d', fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        width={80}
                        tickFormatter={(value) => `$${(value/1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        formatter={(value) => [`$${value.toLocaleString()}`, 'Ventas']}
                        labelFormatter={(label) => new Date(label).toLocaleDateString('es-AR')}
                      />
                      <Bar 
                        dataKey="ventas" 
                        fill={theme.palette.primary.main} 
                        radius={[4, 4, 0, 0]} 
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box sx={{ 
                  height: 300, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexDirection: 'column',
                  color: 'text.secondary'
                }}>
                  <Assessment sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
                  <Typography variant="h6" gutterBottom>
                    Cargando datos de ventas...
                  </Typography>
                  <Typography variant="body2">
                    Los datos se están sincronizando
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Box onClick={() => navigate('/products')} sx={{ cursor: 'pointer' }}>
            <TopProductsChartCard data={productData} />
          </Box>
        </Grid>
      </Grid>

      {/* Sección de Productos y Análisis */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <TopProductsTableCard rows={validatedTopProducts} />
        </Grid>
      </Grid>

      {/* Sección de Insights Ejecutivos */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <ProfitabilityAnalysisCard productsData={validatedTopProducts} />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomerSegmentationCard statsData={statsData} />
        </Grid>
      </Grid>

      {/* Sección de Inteligencia de Negocio */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <RecommendationsCard recommendations={[]} />
        </Grid>
        <Grid item xs={12} md={4}>
          <AlertsCard statsData={statsData} productsData={validatedTopProducts} />
        </Grid>
      </Grid>

      {/* Sección de Análisis Predictivo */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <PredictiveInsightsCard statsData={statsData} salesData={salesData} />
        </Grid>
        <Grid item xs={12} md={6}>
          <CompetitiveAnalysisCard statsData={statsData} />
        </Grid>
      </Grid>

      {/* Carritos Abandonados y Acciones Rápidas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={8}>
          <AbandonedCartsCard data={{
            total: stats?.totalAbandonedCarts || 0,
            totalValue: stats?.totalAbandonedValue || 0,
            potential: stats?.totalAbandonedValue || 0,
          }} />
        </Grid>
        <Grid item xs={12} lg={4}>
          <QuickActionsCard />
        </Grid>
      </Grid>

      {/* Análisis Avanzado y Insights */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={6}>
          <Box onClick={() => navigate('/stats')} sx={{ cursor: 'pointer' }}>
            <AdvancedAnalyticsCard statsData={statsData} />
          </Box>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Box onClick={() => navigate('/products')} sx={{ cursor: 'pointer' }}>
            <InventoryInsightsCard productsData={validatedTopProducts} />
          </Box>
        </Grid>
      </Grid>

      {/* Revenue Trends y Performance */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={8}>
          <Box onClick={() => navigate('/stats')} sx={{ cursor: 'pointer' }}>
            <RevenueTrendsCard salesData={salesData} />
          </Box>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Box onClick={() => navigate('/stats')} sx={{ cursor: 'pointer' }}>
            <PerformanceInsightsCard statsData={statsData} />
          </Box>
        </Grid>
      </Grid>

      {/* Customer Insights Avanzado */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={8}>
          <Box onClick={() => navigate('/customers')} sx={{ cursor: 'pointer' }}>
            <CustomerInsightsCard statsData={statsData} />
          </Box>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Box onClick={() => navigate('/reports')} sx={{ cursor: 'pointer' }}>
            <ReportsAccessCard />
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
}

export default Dashboard;
